import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { fetchWaiverStatus } from '@/store/waiverSlice';
import { sendLoginMessage } from '@/utils/nativeBridge';

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  roles?: string[];
}

/**
 * AuthGuard component for protecting routes based on authentication state
 * 
 * @param children - The components to render if authentication check passes
 * @param requireAuth - Whether authentication is required (default: true)
 * @param redirectTo - Where to redirect if auth check fails (default: '/login' or '/home')
 * @param roles - Optional array of roles that are allowed to access the route
 */
export const AuthGuard = ({
  children,
  requireAuth = true,
  redirectTo,
  roles: _roles // Prefixed with _ to indicate intentionally unused (for future role-based access)
}: AuthGuardProps) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Check if user is authenticated by checking localStorage
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;

  const { activeWaiver, hasSigned } = useSelector((state: RootState) => state.waiver);

  // Send login message to React Native WebView on page load if authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          sendLoginMessage(token, {
            id: user.id || user._id,
            deviceIds: user.deviceIds || []
          });
        }
      } catch (error) {
        console.error('[AuthGuard] Failed to send login message:', error);
      }
    }
  }, [isAuthenticated, token]);

  // Fetch waiver status when authenticated — scoped to member's home location (D-04)
  useEffect(() => {
    if (isAuthenticated && requireAuth) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const homeLocationId = user.locationId;
          if (homeLocationId) {
            dispatch(fetchWaiverStatus(homeLocationId));
          }
          // If no home location, skip waiver check — member is not blocked
        } catch (error) {
          console.error('[AuthGuard] Failed to parse user for waiver check:', error);
        }
      }
    }
  }, [isAuthenticated, requireAuth, dispatch]);

  // Determine where to redirect based on auth state
  const getRedirectPath = () => {
    if (requireAuth && !isAuthenticated) {
      // User needs to be logged in but isn't
      return redirectTo || '/login';
    } else if (!requireAuth && isAuthenticated) {
      // User is logged in but accessing a public-only route
      return redirectTo || '/home';
    }
    return null;
  };

  // Check if user has required role
  const hasRequiredRole = () => {
    // TODO Actual implementation
    return true;
  };

  // Get redirect path based on auth state and role requirements
  const redirectPath = getRedirectPath();

  // Waiver blocking check — redirect unsigned members to /waiver
  // Skip when already on /waiver to prevent infinite redirect loop
  const needsWaiverSignature = isAuthenticated && requireAuth && activeWaiver !== null && !hasSigned;
  if (needsWaiverSignature && location.pathname !== '/waiver') {
    return <Navigate to="/waiver" state={{ from: location }} replace />;
  }

  // If role check fails, redirect to dashboard
  if (isAuthenticated && !hasRequiredRole()) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }
  
  // If auth check fails, redirect to specified path
  if (redirectPath) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }
  
  // Auth check passed, render children
  return <>{children}</>;
};
